import React from "react";
import { Link } from "react-router-dom";
import { Truck, Shield, Package, MapPin, ShoppingBag, Users, TrendingUp } from "lucide-react";
import XPlacement from "@/components/public/XPlacement";
import BrandMark from "@/components/lifted/BrandMark";
import SignupOptionsSection from "@/components/public/SignupOptionsSection";
import PageMeta from "@/components/PageMeta";

const LOOP = [
  { icon: ShoppingBag, t: "Order",   d: "Customers shop the Lifted catalog — gear, accessories, supplies." },
  { icon: Truck,       t: "Deliver", d: "Drivers fulfill same-day in their region. Powered by Lifted." },
  { icon: Users,       t: "Partner", d: "Local shops + creators plug into the same delivery network." },
  { icon: TrendingUp,  t: "Grow",    d: "Everyone earns under one ecosystem — gear, supplies, affiliate." },
];

export default function LiftedSuppliesIntegrated() {
  return (
    <div data-testid="lifted-supplies-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
      <PageMeta title="Lifted Supplies" path="/supplies" ogImage="/og-lifted-supplies.png"
                description="Same-day delivery by Lifted. Order on Lifted. Get delivered by Lifted. The delivery engine of the LiftedAtTheMoment ecosystem." />
      {/* Hero */}
      <header className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center mb-12">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <BrandMark tag="logo.supplies.primary" size="lg" testId="supplies-zone-logo" />
            <div>
              <div className="eyebrow !text-lifted-green">Lifted Supplies</div>
              <div className="text-[0.65rem] text-lifted-mute font-bebas tracking-[0.18em] uppercase">Part of LiftedAtTheMoment</div>
            </div>
          </div>
          <h1 className="font-anton text-4xl sm:text-6xl uppercase mt-4 leading-[0.95]">Same-day delivery,<br /><span className="text-lifted-green">Lifted experience.</span></h1>
          <p className="text-lifted-dim mt-4 max-w-lg">The delivery engine of the LiftedAtTheMoment ecosystem. Order on Lifted. Get delivered by Lifted. Earn alongside Lifted. <strong className="text-lifted-text">One brand. One ecosystem.</strong></p>
          <div className="mt-5 flex flex-wrap gap-2" data-testid="supplies-role-pills">
            <a href="#supplies-customer" data-testid="supplies-pill-customer" className="chip chip--ghost hover:text-lifted-green">I want supplies</a>
            <a href="#supplies-driver"   data-testid="supplies-pill-driver"   className="chip chip--ghost hover:text-lifted-green">I want to drive</a>
            <a href="#supplies-partner"  data-testid="supplies-pill-partner"  className="chip chip--ghost hover:text-lifted-green">My store wants to plug in</a>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/same-day" className="btn-lifted" data-testid="supplies-cta-shop">Order same-day →</Link>
            <Link to="/affiliate-zone" className="btn-ghost" data-testid="supplies-cta-affiliate">Earn alongside it</Link>
          </div>
        </div>
        <div className="flex justify-center">
          <XPlacement zone="supplies" variant="hero" alt="Lifted Supplies" />
        </div>
      </header>

      {/* One-ecosystem loop — anchor targets for the role-routing pills */}
      <section data-testid="supplies-loop" className="mb-14">
        <div className="eyebrow !text-lifted-text-dim">One brand. One ecosystem.</div>
        <h2 className="font-anton text-3xl sm:text-4xl uppercase mt-2 mb-6">Order. Deliver. Partner. Grow.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {LOOP.map((s) => {
            const anchorId = ({ Order: "supplies-customer", Deliver: "supplies-driver",
                               Partner: "supplies-partner", Grow: "supplies-grow" })[s.t];
            return (
              <div key={s.t} id={anchorId} data-testid={`supplies-loop-${s.t.toLowerCase()}`} className="glass-panel p-6 scroll-mt-24">
                <s.icon className="h-6 w-6 text-lifted-green" />
                <div className="font-anton uppercase text-xl mt-3">{s.t}</div>
                <div className="text-xs text-lifted-dim mt-2">{s.d}</div>
              </div>
            );
          })}
        </div>
        <p className="text-[0.7rem] text-lifted-mute mt-5 max-w-2xl">Lifted Supplies is not a separate company — it is the operational arm of LiftedAtTheMoment. Same brand, same community, same standards.</p>
      </section>

      {/* Trust badges */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-14" data-testid="supplies-trust-grid">
        {[
          { icon: Truck,   t: "Same-day delivery",   d: "Orders delivered within hours where available." },
          { icon: Shield,  t: "Age verified",        d: "Secure 21+ verification at checkout & delivery." },
          { icon: Package, t: "Discreet packaging",  d: "Plain packaging — for your privacy." },
          { icon: MapPin,  t: "Real-time tracking",  d: "Track your order every step of the way." },
        ].map((b) => (
          <div key={b.t} className="glass-panel p-6">
            <b.icon className="h-6 w-6 text-lifted-green" />
            <div className="font-anton uppercase text-lg mt-3">{b.t}</div>
            <div className="text-xs text-lifted-dim mt-2">{b.d}</div>
          </div>
        ))}
      </section>

      {/* Tile preview removed in Phase 16.2 — supplies brand mark now lives in the hero. */}

      <section className="text-center max-w-2xl mx-auto" data-testid="supplies-closer">
        <h3 className="font-anton text-2xl sm:text-3xl uppercase">All under LiftedAtTheMoment.</h3>
        <p className="text-lifted-dim mt-3">Order on the same brand. Deliver in the same brand. Earn under the same brand. The ecosystem is one — and you keep what you grow.</p>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Link to="/shop" className="btn-lifted" data-testid="supplies-explore-shop">Browse Lifted</Link>
          <Link to="/affiliate-zone" className="btn-ghost">Affiliate program</Link>
        </div>
      </section>

      {/* Phase 22 — Public signup entry points on the Supplies page */}
      <SignupOptionsSection
        testid="supplies-signup-options"
        title="Plug into Lifted Supplies"
        subtitle="Whether you want to deliver, sell, or refer — pick the role that fits and join the network."
      />
    </div>
  );
}
